// src/pages/Onboarding/slides/TeamDisplaySlide.js
import React, { useEffect, useState } from 'react';
import SlideShell from '../../SlideShell';
import TeamCard from '../../../../components/Onboarding/TeamCard';
import { trackEvent } from '../../../../utils/Tracking';
import { trackMetaLead } from '../../../../utils/metaPixel';
import { trackTikTokContact } from '../../../../utils/tikTokPixel';

// import images (relative: slides -> ../../.. -> src/images)
import alpImage from '../../../../images/alp_image.webp';
import enesImage from '../../../../images/enes_image.webp';

const DEFAULT_TEAM_MEMBERS = [
  {
    id: '1',
    photoSrc: enesImage,
    name: 'Enes Yıldırım',
    title: 'Customer Support',
    description: 'Working with 4 other patients right now',
    fact: 'Consultation help',
  },
  {
    id: '2',
    photoSrc: alpImage,
    name: 'Alp Mungan',
    title: 'Legal Advisor',
    description: 'Working with 6 other patients right now',
    fact: 'Legal & compliance assistance',
  },
];

export default function TeamDisplaySlide({
  title,
  subtitle,
  currentStep,
  totalSteps,
  onNext,
  onBack,
  nextLabel = 'Continue',
  animationDirection = 'forward',
  isTransitioning = false,
  theme = 'dark',

  // per-card loading experience
  loadingDurationMs = 2000,
  revealStaggerMs = 1500,
  loadingText = 'Preparing your care team…',

  teamMembers = DEFAULT_TEAM_MEMBERS,

  // Sync & tracking
  onAutoSave = null,
  sessionId = null,
}) {
  // Handle new chat button clicks (WhatsApp, Messenger, iMessage, Email)
  const handleContactClick = (clickInfo, memberName) => {
    const clickEntry = {
      buttonType: clickInfo.buttonType,
      href: clickInfo.href,
      ariaLabel: clickInfo.ariaLabel,
      slideIndex: 26,
      slideName: 'TeamDisplaySlide',
      memberName: memberName || null,
      clickedAt: new Date().toISOString(),
    };

    // Sync to Firestore via dirty fields
    if (onAutoSave) {
      onAutoSave({ contactButtonClicks: [clickEntry] });
    }

    // Track event (trackEvent uses its own session from sessionStorage)
    trackEvent('new_chat_button_clicked', {
      button_type: clickInfo.buttonType,
      href: clickInfo.href || null,
      source_type: 'onboarding',
      source_detail: 'team_display_slide',
      onboarding_id: sessionId || null,
      slide_number: 26,
      member_name: memberName || null,
    });
    // Track TikTok Pixel Contact event
    trackMetaLead({ content_name: 'team_display_contact', content_category: 'onboarding_contact' });
    trackTikTokContact('onboarding_team_display');
  };
  // Show fake loading only on first visit per session
  const TEAM_SLIDE_SEEN_KEY = 'mm_onboarding_seen_team_slide_v1';
  const [hasSeen, setHasSeen] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.sessionStorage.getItem(TEAM_SLIDE_SEEN_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const [revealed, setRevealed] = useState(() => teamMembers.map(() => (hasSeen ? true : false)));

  // Persist as seen only AFTER the first reveal completes (first-time only)
  useEffect(() => {
    if (hasSeen) return; // already marked
    const allShown = revealed.every(Boolean);
    if (!allShown) return;
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(TEAM_SLIDE_SEEN_KEY, 'true');
      setHasSeen(true);
    } catch {
      // no-op
    }
  }, [revealed, hasSeen]);

  // When team changes, reveal immediately if already seen; else reset to hidden
  useEffect(() => {
    setRevealed(teamMembers.map(() => (hasSeen ? true : false)));
  }, [teamMembers, hasSeen]);

  useEffect(() => {
    // Skip timers on revisits
    if (hasSeen) return;

    const timers = teamMembers.map((_, idx) =>
      setTimeout(() => {
        setRevealed((prev) => {
          if (prev[idx]) return prev;
          const next = [...prev];
          next[idx] = true;
          return next;
        });
      }, loadingDurationMs + idx * revealStaggerMs)
    );
    return () => timers.forEach((t) => clearTimeout(t));
  }, [teamMembers, loadingDurationMs, revealStaggerMs, hasSeen]);

  const isDark = theme === 'dark';
  const pageBg = isDark ? 'bg-mm_black' : 'bg-white';
  const textMuted = isDark ? 'text-gray-300' : 'text-gray-600';
  const spinnerColor = isDark ? 'text-white' : 'text-gray-800';
  const ringColor = isDark ? 'text-gray-700' : 'text-gray-300';
  const hasMembers = teamMembers.length > 0;
  const allRevealed = revealed.every(Boolean);

  return (
    <div className={`min-h-screen ${pageBg}`}>
      <SlideShell
        title={title}
        subtitle={subtitle}
        currentStep={currentStep}
        totalSteps={totalSteps}
        onNext={onNext}
        onBack={onBack}
        nextLabel={nextLabel}
        theme={theme}
        animationDirection={animationDirection}
        isTransitioning={isTransitioning}
        disabled={hasMembers ? !allRevealed : false}
      >
        {/* Card reveal animations */}
        <style>{`
          @keyframes fadeSlideUp {
            0% { opacity: 0; transform: translateY(14px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @media (prefers-reduced-motion: reduce) {
            .cardReveal { animation: fadeSlideUp 1ms linear forwards !important; }
          }
        `}</style>

        <div className="flex flex-col gap-4">
          {teamMembers.map((m, idx) => {
            const isShown = revealed[idx];
            return (
              <div key={m.id}>
                {!isShown ? (
                  // Loading placeholder inside card frame
                  <div className="w-full rounded-2xl border-2 border-white bg-gray-900 p-6">
                    <div className="flex items-center justify-between">
                      <div className={`text-sm ${textMuted}`}>{loadingText}</div>
                      <svg
                        className={`animate-spin h-6 w-6 ${spinnerColor}`}
                        viewBox="0 0 24 24"
                        role="status"
                        aria-label={loadingText}
                      >
                        <circle
                          className={`opacity-25 ${ringColor}`}
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        />
                      </svg>
                    </div>
                  </div>
                ) : (
                  <div
                    className="opacity-0 cardReveal"
                    style={{ animation: 'fadeSlideUp 650ms cubic-bezier(0.22, 1, 0.36, 1) forwards' }}
                  >
                    <TeamCard
                      slot={idx === 0 ? 'first' : idx === 1 ? 'second' : undefined}
                      photoSrc={m.photoSrc}
                      name={m.name}
                      title={m.title}
                      description={m.description}
                      fact={m.fact}
                      theme={theme}
                      onContactClick={(clickInfo) => handleContactClick(clickInfo, m.name)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SlideShell>
    </div>
  );
}
